import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { Box, Paper, Skeleton, Typography } from "@mui/material";
import { CheckCircleOutline, InfoOutlined, Phone } from "@mui/icons-material";
import * as Sentry from "@sentry/react";
import dayjs from "dayjs";
import {
  Callout,
  ConsentRow,
  EditPhoneDialog,
  EditPhoneValues,
  InfoBanner,
  OtpInput,
  PageHeader,
  SkeletonTextoLegal,
  StepActions,
  TypographyLegal,
  TypographyOtpHelp,
} from "@components/ui_v2";
import { ConfirmationDialog } from "@components/ConexionSat/ConfirmationDialog";
import { RootState, useAppDispatch } from "@store/store";
import {
  setBuroNipPM,
  setBuroNipPMLastCodeRequest,
  setCheckSatBureau,
  setShareholderUpdatePhone,
} from "@store/slices/registerSlice";
import { setModalErrorVisible } from "@store/slices/appSlice";
import { useRequestPhoneCodeMutation, useValidatePhoneCodeMutation } from "@api/onboarding";
import { usePatchPersonMutation } from "@api/personasApi";
import { CiecValidatorRequest, useCiecValidatorMutation } from "@api/sat_apis_new/satApsi";
import { usePostValidateNextStageMutation } from "@api/applications";
import { ApiError } from "@interfaces/response";
import { TAX_SYSTEM_TYPE } from "@helpers/constantes";
import { formatPhoneNumber, getRemainingTime, normalizeUrl } from "@helpers/funciones";
import useStatusConfig from "@hooks/useStatusConfig";
import useTextosLegales from "@hooks/useTextosLegales";
import useTipoPersona from "@hooks/useTipoPersona";

const TIME_LIMIT = 120;

/**
 * "Autoriza la consulta al buró de la empresa" (`S_NIPBuro` PM del prototipo de Mercader). Lógica del legacy
 * `tofu/pm/ConsultaBuroNipPM` + `ModalBuroNipPM`, pero el código va al teléfono del representante legal (la persona en
 * `shareholder`, como en `ConsultaBuro`); se guarda en `buroNipPM` y,
 * al autorizar, se manda la consulta de la empresa con el validador de CIEC sin contraseña (`validate_ciec: false`).
 * El status sale del `status_config` (la entrada que apunta a esta url), así que no se fija aquí.
 */
export const ConsultaBuroPm = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();

  const statusConfig = useSelector((state: RootState) => state.app.status_config);
  const statusCode =
    statusConfig.find((item) => normalizeUrl(item.url) === normalizeUrl(location.pathname))?.code ?? "";
  const { nextStatus, previousStatus } = useStatusConfig(statusCode);

  const buroNip = useSelector((state: RootState) => state.register.buroNipPM);
  // Textos legales de la empresa (`buro_empresa`); mientras cargan, skeleton en lugar del texto por defecto.
  const { textosLegales, cargandoTextos } = useTextosLegales("ConsultaBuroPm");
  const accountUser = useSelector((state: RootState) => state.register.account_user);
  const application = useSelector((state: RootState) => state.register.application);
  const company = useSelector((state: RootState) => state.register.company);
  const brand = useSelector((state: RootState) => state.brand);
  // El representante legal (`DatosRepresentanteLegal`): quien autoriza con el NIP.
  const representante = useSelector((state: RootState) => state.register.shareholder);
  // Tipo de la persona que autoriza, para el texto de éxito ("…del representante legal"); `null` mientras se consulta.
  const tipoPersona = useTipoPersona(representante.id, "ConsultaBuroPm");
  const phone = { countryCode: representante.code_phone_number, number: representante.phone_number };

  const [validatePhoneCode] = useValidatePhoneCodeMutation();
  const [requestPhoneCode, { isLoading: isRequestingCode }] = useRequestPhoneCodeMutation();
  const [patchPerson] = usePatchPersonMutation();
  const [ciecValidation] = useCiecValidatorMutation();
  const [triggerPostNextStage] = usePostValidateNextStageMutation();

  const resendEffectRanRef = useRef(false);

  const [otp, setOtp] = useState("");
  const [modalOtp, setModalOtp] = useState("");
  const [secondsRemaining, setSecondsRemaining] = useState(TIME_LIMIT);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [rateLimited, setRateLimited] = useState(false);
  const [justResent, setJustResent] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [openEditPhone, setOpenEditPhone] = useState(false);

  const otpConfirmCorrecto = modalOtp.length === 4 && modalOtp === buroNip.code;
  const autorizado = buroNip.termsAccepted;

  useEffect(() => {
    if (secondsRemaining <= 0) return;
    const t = setTimeout(() => setSecondsRemaining((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [secondsRemaining]);

  // Si el último envío fue hace menos de 2 min, solo se retoma la cuenta (como el legacy).
  const handleResendCode = async () => {
    try {
      setIsLoading(true);
      setRateLimited(false);

      if (buroNip.isValid || isRequestingCode || !phone.countryCode || !phone.number) return;

      const secondsToWait = getRemainingTime(buroNip.last_code_request || "", TIME_LIMIT);
      if (secondsToWait > 0) {
        setSecondsRemaining(secondsToWait);
        return;
      }

      dispatch(setBuroNipPMLastCodeRequest({ last_code_request: dayjs().toISOString() }));
      try {
        await requestPhoneCode({ country_code: phone.countryCode, phone_number: phone.number }).unwrap();
      } catch (requestError) {
        if ((requestError as ApiError)?.status !== 429) {
          dispatch(setBuroNipPMLastCodeRequest({ last_code_request: null }));
        }
        throw requestError;
      }

      setSecondsRemaining(TIME_LIMIT);
      setJustResent(true);
      setTimeout(() => setJustResent(false), 4000);
    } catch (error) {
      const apiError = error as ApiError;
      if (apiError?.status === 429) {
        setRateLimited(true);
        return;
      }
      Sentry.captureException(error, { extra: { component: "BuroEmpresaPm-resend" } });
      dispatch(setModalErrorVisible({ open: true, error: apiError }));
    } finally {
      setIsLoading(false);
    }
  };

  const validateCode = async () => {
    try {
      if (buroNip.isValid) {
        setErrorMessage("");
        return;
      }
      if (otp.length < 4) return;
      if (!phone.countryCode || !phone.number) {
        setErrorMessage("No se encontró el número de teléfono");
        return;
      }

      setIsLoading(true);
      setErrorMessage("");

      await validatePhoneCode({
        country_code: phone.countryCode,
        phone_number: phone.number,
        code: otp,
      }).unwrap();

      dispatch(setBuroNipPM({ code: otp, isValid: true, termsAccepted: false }));
    } catch (error) {
      const apiError = error as ApiError;
      if (apiError?.status === 404 || apiError?.status === 400) {
        setErrorMessage("Código incorrecto, intenta de nuevo");
        setOtp("");
        return;
      }
      Sentry.captureException(apiError, { extra: { component: "BuroEmpresaPm-validate", values: { otp } } });
      dispatch(setModalErrorVisible({ open: true, error: apiError }));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    validateCode();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [otp]);

  // Al entrar se manda el código (si no está validado); si ya lo estaba, se muestra.
  useEffect(() => {
    if (!buroNip.isValid && !resendEffectRanRef.current) {
      resendEffectRanRef.current = true;
      handleResendCode();
    }
    if (buroNip.isValid && buroNip.code) {
      setOtp(buroNip.code);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [buroNip.isValid]);

  // Con el código validado, el check abre la confirmación; una vez autorizado ya no se quita.
  const handleConsentClick = () => {
    if (!buroNip.isValid || buroNip.termsAccepted) return;
    setModalOtp("");
    setShowConfirmModal(true);
  };

  const handleAcceptConsent = () => {
    if (!otpConfirmCorrecto) return;
    dispatch(setBuroNipPM({ code: buroNip.code, isValid: true, termsAccepted: true }));
    setShowConfirmModal(false);
    setModalOtp("");
  };

  const handleSavePhone = async (values: EditPhoneValues) => {
    try {
      setIsLoading(true);
      if (!representante.id) {
        throw new Error("No se encontró al representante legal");
      }
      const payload = { code_phone_number: values.code_phone_number, phone_number: values.phone_number };
      await patchPerson({ personId: String(representante.id), body: payload }).unwrap();
      await requestPhoneCode({ country_code: values.code_phone_number, phone_number: values.phone_number }).unwrap();

      dispatch(setShareholderUpdatePhone(payload));
      dispatch(setBuroNipPMLastCodeRequest({ last_code_request: dayjs().toISOString() }));
      setOpenEditPhone(false);
      setSecondsRemaining(TIME_LIMIT);
      setOtp("");
    } catch (error) {
      const apiError = error as ApiError;
      if (apiError?.status === 429) {
        setRateLimited(true);
        setOpenEditPhone(false);
        return;
      }
      Sentry.captureException(apiError, { extra: { component: "BuroEmpresaPm-change_phone" } });
      dispatch(setModalErrorVisible({ open: true, error: apiError }));
    } finally {
      setIsLoading(false);
    }
  };

  const continueToNextStatus = async () => {
    if (!nextStatus) throw new Error("No se encontró el siguiente status");

    await triggerPostNextStage({
      applicationId: application.id,
      statusCode: nextStatus.code,
      username: `TOFU - ${accountUser.email}`,
    }).unwrap();

    dispatch(setCheckSatBureau({ check_sat_bureau: false }));
    navigate(nextStatus.url);
  };

  // Consulta de la empresa como el legacy: validador de CIEC sin contraseña para el RFC de la empresa.
  const handleContinue = async () => {
    if (!buroNip.isValid || !buroNip.termsAccepted) return;

    setIsLoading(true);
    try {
      const payload: CiecValidatorRequest = {
        rfc: company.rfc || "",
        ciec: "",
        application_code: application.company_application_code,
        client_details_id: String(application.client_details_id),
        loan_application_conditions_id: String(application.application_condition_id),
        loan_application_id: String(application.id),
        requested_amount: Number(application.loan_amount),
        requested_term: Number(application.loan_term),
        validate_ciec: false,
        company_id: brand?.id ?? "",
        connection_is_mobile_app: true,
        is_ciec_required: false,
      };
      await ciecValidation(payload).unwrap();
      await continueToNextStatus();
    } catch (error) {
      const apiError = error as ApiError;
      // 409: la consulta ya se había hecho; se sigue igual que en el legacy.
      if (apiError.status === 409) {
        try {
          await continueToNextStatus();
        } catch (continueError) {
          Sentry.captureException(continueError, { extra: { component: "BuroEmpresaPm-continue" } });
          dispatch(setModalErrorVisible({ open: true, error: continueError as ApiError }));
        }
        return;
      }
      Sentry.captureException(error, { extra: { component: "BuroEmpresaPm-consulta" } });
      dispatch(setModalErrorVisible({ open: true, error: apiError }));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box>
      {/* `S_NIPBuro` del prototipo, versión PM. */}
      <PageHeader
        eyebrow="Autorización · Buró de crédito"
        title="Autoriza la consulta al buró de la empresa"
        subtitle="El representante legal autoriza la consulta con un NIP. Enviamos un código de 4 dígitos por SMS y WhatsApp a su teléfono."
      />

      <Box sx={{ mb: 2 }}>
        <InfoBanner
          icon={<Phone fontSize="small" />}
          label="Teléfono del representante legal"
          value={phone.number ? `(${phone.countryCode}) ${formatPhoneNumber(String(phone.number))}` : "—"}
          editLabel="Cambiar"
          onEdit={buroNip.isValid ? undefined : () => setOpenEditPhone(true)}
        />
      </Box>

      {autorizado ? (
        <Paper variant="lendia_paper" sx={{ textAlign: "center", p: "32px 24px" }}>
          <Box
            sx={{
              width: 52,
              height: 52,
              borderRadius: "50%",
              bgcolor: "success.light",
              color: "success.main",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mx: "auto",
              mb: 2,
            }}
          >
            <CheckCircleOutline sx={{ fontSize: 26 }} />
          </Box>
          <Typography sx={{ fontSize: 18, fontWeight: 600, letterSpacing: "-0.01em", color: "primary.dark" }}>
            Autorización registrada
          </Typography>
          <Typography sx={{ fontSize: 13, color: "tokens.textTertiary", lineHeight: 1.55, mt: 1, mb: 2.5 }}>
            {tipoPersona === null ? (
              <Skeleton variant="text" animation="wave" />
            ) : (
              `Hemos confirmado la autorización del ${tipoPersona || "representante legal"} para consultar a la empresa en Dun & Bradstreet.`
            )}
          </Typography>
          <ConsentRow
            checked
            onChange={() => undefined}
            disabled
            label={
              cargandoTextos ? (
                <SkeletonTextoLegal lineas={2} />
              ) : (
                <TypographyLegal
                  type="autorizacionBuro"
                  taxSystemType={TAX_SYSTEM_TYPE.PM.code}
                  texto={textosLegales.buro_empresa?.check_buro?.texto}
                />
              )
            }
          />
        </Paper>
      ) : (
        <>
          {/* `.ld-verify-card`: la misma tarjeta del código que la validación de teléfono y el NIP. */}
          <Paper
            variant="lendia_paper"
            sx={{
              textAlign: "center",
              p: "36px 28px 28px",
              boxShadow: "0 1px 2px rgba(14, 31, 58, 0.06), 0 1px 3px rgba(14, 31, 58, 0.04)",
            }}
          >
            <Box sx={{ display: "flex", justifyContent: "center" }}>
              <OtpInput length={4} value={otp} onChange={setOtp} size="lg" disabled={isLoading || buroNip.isValid} />
            </Box>

            {errorMessage && (
              <Box sx={{ mt: 2 }}>
                <Callout tone="warn">{errorMessage}</Callout>
              </Box>
            )}

            {rateLimited && (
              <Box sx={{ mt: 2 }}>
                <Callout tone="warn">
                  Excedió el número de intentos permitidos para ese número, intenta más tarde o contacta a un asesor.
                </Callout>
              </Box>
            )}

            {buroNip.isValid ? (
              <Typography sx={{ mt: 2, fontSize: 13, fontWeight: 500, color: "success.main" }}>
                Código verificado
              </Typography>
            ) : (
              <TypographyOtpHelp
                mt={2}
                justResent={justResent}
                secondsRemaining={secondsRemaining}
                onResend={handleResendCode}
                resendDisabled={isLoading}
                testIdPrefix="buro-empresa"
              />
            )}
          </Paper>

          <Paper variant="lendia_paper" sx={{ mt: 2, display: "flex", flexDirection: "column", gap: 1.25 }}>
            <ConsentRow
              bare
              checked={false}
              onChange={handleConsentClick}
              disabled={!buroNip.isValid || isLoading || cargandoTextos}
              label={
                cargandoTextos ? (
                  <SkeletonTextoLegal lineas={2} />
                ) : (
                  <TypographyLegal
                    type="autorizacionBuro"
                    taxSystemType={TAX_SYSTEM_TYPE.PM.code}
                    texto={textosLegales.buro_empresa?.check_buro?.texto}
                  />
                )
              }
            />
            <Callout tone="info" icon={<InfoOutlined sx={{ fontSize: 16 }} />}>
              <Box component="span" sx={{ fontSize: 12.5, lineHeight: 1.55 }}>
                {cargandoTextos ? (
                  <SkeletonTextoLegal lineas={3} />
                ) : (
                  <TypographyLegal type="avisoEvaluacion" texto={textosLegales.buro_empresa?.aviso?.texto} />
                )}
              </Box>
            </Callout>
          </Paper>
        </>
      )}

      <StepActions
        onBack={() => previousStatus && navigate(previousStatus.url)}
        onNext={handleContinue}
        nextLabel={autorizado ? "Continuar al análisis" : "Continuar"}
        nextDisabled={!autorizado || isLoading}
        backTestId="buro-empresa-back-button"
        nextTestId="buro-empresa-continue-button"
      />

      <ConfirmationDialog
        open={showConfirmModal}
        title={textosLegales.buro_empresa?.modal_buro?.titulo?.trim() || "Consulta a Buró de Crédito"}
        onClose={() => setShowConfirmModal(false)}
        onAccept={handleAcceptConsent}
        closeLabel="Cancelar"
        acceptLabel="Acepto y autorizo"
        acceptDisabled={!otpConfirmCorrecto}
      >
        <Typography component="div" sx={{ fontSize: 13.5, color: "tokens.textSecondary", lineHeight: 1.65, mb: 2 }}>
          {cargandoTextos ? (
            <SkeletonTextoLegal lineas={8} />
          ) : (
            <TypographyLegal
              type="modalBuro"
              taxSystemType={TAX_SYSTEM_TYPE.PM.code}
              parrafos={textosLegales.buro_empresa?.modal_buro?.parrafos}
              secciones={textosLegales.buro_empresa?.modal_buro?.secciones}
            />
          )}
        </Typography>
        <Box sx={{ borderTop: "1px solid", borderColor: "divider", pt: 2 }}>
          <Typography sx={{ fontSize: 13, fontWeight: 500, color: "primary.dark", mb: 1.25 }}>
            Confirma tu identidad reingresando el código OTP
          </Typography>
          <OtpInput length={4} value={modalOtp} onChange={setModalOtp} />
          {modalOtp.length === 4 && !otpConfirmCorrecto && (
            <Typography sx={{ fontSize: 12.5, color: "error.main", mt: 1 }}>
              Código incorrecto. Inténtalo de nuevo.
            </Typography>
          )}
        </Box>
      </ConfirmationDialog>

      <EditPhoneDialog
        open={openEditPhone}
        initialValues={{ code_phone_number: phone.countryCode || "+52", phone_number: phone.number || "" }}
        onSubmit={handleSavePhone}
        onClose={() => setOpenEditPhone(false)}
        disabled={buroNip.isValid || isLoading}
      />
    </Box>
  );
};
